import React, { useState } from "react";
import { useTranslation } from "@/i18n/LanguageContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface AnswerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (content: string) => void;
  isSubmitting: boolean;
}

export function AnswerDialog({ isOpen, onClose, onSubmit, isSubmitting }: AnswerDialogProps) {
  const { t } = useTranslation();
  const [answerContent, setAnswerContent] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!answerContent.trim()) return;
    onSubmit(answerContent);
  };

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("desafios_cacatais.answer_dialog.title", { fallback: "Add an answer" })}</DialogTitle>
          <DialogDescription>
            {t("desafios_cacatais.answer_dialog.description", { fallback: "Share your solution or explanation with others. This will be visible to everyone." })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="answerContent">{t("desafios_cacatais.answer_dialog.content", { fallback: "Your answer" })}</Label>
              <textarea
                id="answerContent"
                value={answerContent}
                onChange={(e) => setAnswerContent(e.target.value)}
                placeholder={t("desafios_cacatais.answer_dialog.content_placeholder", { fallback: "Explain how you solved this puzzle..." })}
                className="bg-zinc-800 border-zinc-700 rounded-md p-2 min-h-[120px] text-white"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {t("desafios_cacatais.answer_dialog.cancel", { fallback: "Cancel" })}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("desafios_cacatais.answer_dialog.submitting", { fallback: "Submitting..." })}
                </>
              ) : (
                t("desafios_cacatais.answer_dialog.submit", { fallback: "Submit" })
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
